import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useSWR from "swr";
import { process } from "../Process";
import { Permission, } from "ee-nestjs-interfaces";
import { useNavigate, useParams } from "react-router-dom";
import { Tab } from "@headlessui/react";
import "react-datetime-picker/dist/DateTimePicker.css";
import "react-calendar/dist/Calendar.css";
import "react-clock/dist/Clock.css";
import { Auth0Fetcher, ExpandableHelpTooltip, SingleStat, } from "ee-react-components";
import { NOCStationControls } from "../Components/Tabs/NocStationControls";
import { NOCCommands } from "../Components/Tabs/NocCommands";
import { StationOverview } from "../Components/Tabs/StationOverview";
import { BackButton } from "../Components/BackButton";
import { Transactions } from "../Components/Tabs/Transactions";
import { Usage } from "../Components/Tabs/Usage";
import { Pricing } from "../Components/Tabs/Pricing";
import { TabStyle } from "../Components/TabStyle";
import { BigCircle } from "../Components/Images";
import { StationPower } from "../Components/Tabs/StationPower";
import { SiteSettingsTab } from "../Components/Tabs/SiteSettings";
import { SiteAlerts } from "../Components/Tabs/SiteAlerts";
import SiteOrganizations from "../Components/Tabs/SiteOrganizations";
import { Outages } from "../Components/Tabs/Outages";
import { usePermissions } from "../PermissionsContext";
import { ReportsTab } from "../Components/Tabs/Reports";
import { SiteBanner } from "../Components/SiteBanner";
import { IncidentsPane } from "../Components/Incidents/IncidentsPane";
// Station drilldown page.
export var Station = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var isNoc = _a.isNoc, organizationSettings = _a.organizationSettings;
    var stationId = useParams().stationId;
    var navigate = useNavigate();
    var siteId = (_b = Number(stationId)) !== null && _b !== void 0 ? _b : -1;
    var permissions = usePermissions();
    var hasReadSiteSettings = permissions.hasPermissionForSite(Permission.READ_SITE_SETTINGS, siteId);
    var hasReadPricing = permissions.hasPermissionForSite(Permission.READ_PRICING, siteId);
    var hasReadReports = permissions.hasPermissionForSite(Permission.READ_REPORTS, siteId);
    var hasReadPowerTrace = permissions.hasPermissionForSite(Permission.READ_POWER_TRACE, siteId);
    var hasNocWriteAccess = permissions.hasPermissionForSite(Permission.WRITE_NOC, siteId);
    var _k = useAuth0(), isAuthenticated = _k.isAuthenticated, isLoading = _k.isLoading, getAccessTokenSilently = _k.getAccessTokenSilently;
    var details = useSWR(isLoading || !isAuthenticated
        ? null
        : [
            "".concat(process.env.CORE_SVC_URL, "/site/detail/").concat(stationId),
            getAccessTokenSilently,
        ], Auth0Fetcher).data;
    // The site being viewed may belong to a different organization than the
    // logged-in user's own (e.g. a NOC user), so tiered pricing must be keyed
    // off the site's owner organization rather than `organizationSettings`
    // (which reflects the logged-in user's organization).
    var siteOrganizationFeatures = useSWR(isLoading || !isAuthenticated || !(details === null || details === void 0 ? void 0 : details.ownerOrganization)
        ? null
        : [
            "".concat(process.env.CORE_SVC_URL, "/organization/features/").concat(details.ownerOrganization.id),
            getAccessTokenSilently,
        ], Auth0Fetcher).data;
    var tab = useParams().tab;
    var tabIndex = tab ? parseInt(tab) : undefined;
    var complianceReportingEnabled = (_c = organizationSettings === null || organizationSettings === void 0 ? void 0 : organizationSettings.complianceReportingEnabled) !== null && _c !== void 0 ? _c : false;
    var showReportsTab = hasReadReports && (isNoc || complianceReportingEnabled);
    var tieredPricingEnabled = (_d = siteOrganizationFeatures === null || siteOrganizationFeatures === void 0 ? void 0 : siteOrganizationFeatures.tieredPricingEnabled) !== null && _d !== void 0 ? _d : false;
    var alerts = useSWR(!isNoc || isLoading || !isAuthenticated
        ? null
        : [
            "".concat(process.env.NOC_SVC_URL, "/alerts/list/").concat(stationId),
            getAccessTokenSilently,
        ], Auth0Fetcher).data;
    var siteBanners = useSWR(isLoading || !isAuthenticated
        ? null
        : [
            "".concat(process.env.CORE_SVC_URL, "/banners/site/").concat(stationId),
            getAccessTokenSilently,
        ], Auth0Fetcher).data;
    var tooltip = isNoc && (React.createElement(SingleStat, { title: "Lockbox Code", value: "".concat(details === null || details === void 0 ? void 0 : details.lockboxCode), center: true, monospace: true }));
    return (React.createElement("div", { className: "flex flex-col gap-y-[64px] p-[32px]" },
        React.createElement(BackButton, null),
        details && (React.createElement("div", { className: "flex flex-col gap-y-[8px]" },
            React.createElement("label", { className: "font-N4 text-white" }, details.name),
            React.createElement("div", { className: "flex flex-row gap-x-[8px]" },
                BigCircle,
                React.createElement("label", { className: "font-N8 text-black-600" }, "".concat(details.activeSessions, " Active Session").concat(details.activeSessions != 1 ? "s" : ""))),
            React.createElement("div", { className: "flex flex-row gap-x-[8px]" },
                React.createElement("label", { className: "font-N8 text-black-600 mt-[16px]" },
                    details.addressLine1,
                    React.createElement("br", null),
                    details.addressLine2),
                React.createElement("div", { className: "font-N8 text-black-600 h-0 translate-y-[23px]" }, isNoc && details.lockboxCode && (React.createElement(ExpandableHelpTooltip, { tooltip: tooltip })))))),
        React.createElement(SiteBanner, { banners: siteBanners }),
        details && (React.createElement("div", null,
            React.createElement(Tab.Group, { selectedIndex: tabIndex, onChange: function (index) { return navigate("/station/".concat(stationId, "/").concat(index)); } },
                React.createElement(Tab.List, { className: "flex flex-row flex-wrap gap-x-[16px] gap-y-[16px] mb-[64px]" },
                    React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return TabStyle(selected);
                        } }, "Overview"),
                    React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return TabStyle(selected);
                        } }, "Transactions"),
                    hasReadPowerTrace && (React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return TabStyle(selected);
                        } }, "Power")),
                    React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return TabStyle(selected);
                        } }, "Usage"),
                    hasReadPricing && (React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return TabStyle(selected);
                        } }, "Pricing")),
                    hasReadSiteSettings && (React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return TabStyle(selected);
                        } }, "Site Settings")),
                    showReportsTab && (React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return TabStyle(selected);
                        } }, "Reports")),
                    isNoc && (React.createElement(React.Fragment, null,
                        React.createElement(Tab, { className: function (_a) {
                                var selected = _a.selected;
                                return TabStyle(selected);
                            } }, "Asset Controls"))),
                    isNoc && hasNocWriteAccess && (React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return TabStyle(selected);
                        } }, "Commanding")),
                    isNoc && hasNocWriteAccess && (React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return TabStyle(selected);
                        } }, "Organizations")),
                    isNoc && (React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return "".concat(TabStyle(selected)).concat(!selected &&
                                alerts != null &&
                                alerts.activeAlerts.length > 0
                                ? " !text-red"
                                : "");
                        } }, "Alerts".concat(alerts != null && alerts.activeAlerts.length > 0
                        ? " (".concat(alerts.activeAlerts.length, ")")
                        : ""))),
                    isNoc && hasNocWriteAccess && (React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return TabStyle(selected);
                        } }, "Outages")),
                    isNoc && (React.createElement(Tab, { className: function (_a) {
                            var selected = _a.selected;
                            return TabStyle(selected);
                        } }, "Incidents"))),
                React.createElement(Tab.Panels, null,
                    React.createElement(Tab.Panel, null,
                        React.createElement(StationOverview, { details: details, isNoc: isNoc })),
                    React.createElement(Tab.Panel, null,
                        React.createElement(Transactions, { resourceUrlBase: "".concat(process.env.CORE_SVC_URL, "/charging-sessions/attempts/site/").concat(details.id), earliestTransaction: (_e = details.onlineSince) !== null && _e !== void 0 ? _e : new Date(), isNoc: isNoc })),
                    hasReadPowerTrace && (React.createElement(Tab.Panel, null,
                        React.createElement(StationPower, { details: details, isNoc: isNoc, startDate: (_f = details.onlineSince) !== null && _f !== void 0 ? _f : new Date() }))),
                    React.createElement(Tab.Panel, null,
                        React.createElement(Usage, { resourceUrl: "".concat(process.env.CORE_SVC_URL, "/site/").concat(details.id, "/stats"), startDate: (_h = (_g = details.expectedDateOnline) !== null && _g !== void 0 ? _g : details.onlineSince) !== null && _h !== void 0 ? _h : new Date() })),
                    hasReadPricing && (React.createElement(Tab.Panel, null,
                        React.createElement(Pricing, { details: details, tieredPricingEnabled: tieredPricingEnabled }))),
                    hasReadSiteSettings && (React.createElement(Tab.Panel, null,
                        React.createElement(SiteSettingsTab, { details: details }))),
                    showReportsTab && (React.createElement(Tab.Panel, null,
                        React.createElement(ReportsTab, { details: details, isNoc: isNoc, complianceReportingEnabled: complianceReportingEnabled }))),
                    isNoc && (React.createElement(React.Fragment, null,
                        React.createElement(Tab.Panel, null,
                            React.createElement(NOCStationControls, { details: details })))),
                    isNoc && hasNocWriteAccess && (React.createElement(Tab.Panel, null,
                        React.createElement(NOCCommands, { details: details, isNoc: isNoc }))),
                    isNoc && hasNocWriteAccess && (React.createElement(Tab.Panel, null,
                        React.createElement(SiteOrganizations, { details: details }))),
                    isNoc && (React.createElement(Tab.Panel, null,
                        React.createElement(SiteAlerts, { details: details, alerts: alerts }))),
                    isNoc && hasNocWriteAccess && (React.createElement(Tab.Panel, null,
                        React.createElement(Outages, { resourceUrlBase: "".concat(process.env.NOC_SVC_URL, "/outages/").concat(details.id), siteId: details.id, earliestStatus: (_j = details.onlineSince) !== null && _j !== void 0 ? _j : new Date(), chargerDetails: details.chargers }))),
                    isNoc && (React.createElement(Tab.Panel, null,
                        React.createElement(IncidentsPane, { siteId: details.id, canWrite: hasNocWriteAccess, chargers: details.chargers, powernodes: details.powernodes })))))))));
};
export var StationRedirect = function (_a) {
    var organizationId = _a.organizationId;
    var _b = useAuth0(), isAuthenticated = _b.isAuthenticated, isLoading = _b.isLoading, getAccessTokenSilently = _b.getAccessTokenSilently;
    var navigate = useNavigate();
    var queryParams = new URLSearchParams(window.location.search);
    // Can redirect by either pnid OR the site that contains ocppid
    var pnid = queryParams.get("pnid");
    var ocppid = queryParams.get("ocppid");
    if ((pnid == null || isNaN(Number(pnid))) && ocppid == null) {
        console.log("no valid parameters");
        navigate("/stations");
        return null;
    }
    var _c = useSWR(isLoading || !isAuthenticated
        ? null
        : [
            "".concat(process.env.CORE_SVC_URL, "/site/list/").concat(organizationId),
            getAccessTokenSilently,
        ], Auth0Fetcher), siteList = _c.data, isLoadingList = _c.isLoading;
    if (isLoadingList) {
        return null;
    }
    var filter;
    if (pnid) {
        var pnidNum_1 = Number(pnid);
        filter = function (site) {
            return site.powernodeIds.includes(pnidNum_1);
        };
    }
    else if (ocppid) {
        filter = function (site) {
            return site.chargerOcppIds.includes(ocppid);
        };
    }
    else {
        filter = function () {
            return false;
        };
    }
    var filteredSiteList = siteList === null || siteList === void 0 ? void 0 : siteList.sites.filter(filter);
    if ((filteredSiteList === null || filteredSiteList === void 0 ? void 0 : filteredSiteList.length) == 1) {
        navigate("/station/" + filteredSiteList[0].id);
    }
    else if ((filteredSiteList === null || filteredSiteList === void 0 ? void 0 : filteredSiteList.length) == 0) {
        console.log("Could not find site");
        navigate("/stations");
    }
    else {
        // All fields should have uniqueness constraints, but jic:
        console.log("Could not find site, multiple sites matched");
        navigate("/stations");
    }
    return null;
};
